/**
 * Feature-flag override model for the internal toolbar.
 *
 * Flags live in redux (`state.featureFlags`), seeded server-side per request
 * from `window.INITIAL_STATE`. The toolbar reads the live redux value and
 * writes session-only overrides back through the same `storeFlags` action the
 * server bootstrap uses — so a toggle is reactive across every connected
 * consumer instantly, with zero bespoke wiring.
 *
 * "Baseline" = the server-resolved value captured once at page load. We diff
 * live-vs-baseline to surface which flags are locally overridden, and to offer
 * a reset. Overrides are in-memory only; a refresh restores server truth.
 */
import { useCallback, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { actions } from '@adc/ui-morpheus'

// ui-morpheus ships untyped JS; pin the one action we use to a typed shape so
// dispatch stays type-safe (avoids no-unsafe-call on the module boundary).
const storeFlags = (
  actions as {
    featureFlags: {
      storeFlags: (flags: Record<string, boolean>) => {
        type: string
        flags: Record<string, boolean>
      }
    }
  }
).featureFlags.storeFlags

interface StoreShape {
  featureFlags?: Record<string, boolean>
}

interface InjectedState {
  featureFlags?: Record<string, boolean>
}

/** Stable empty reference so an absent slice doesn't churn hook deps. */
const EMPTY_FLAGS: Record<string, boolean> = {}

export interface FlagRow {
  readonly key: string
  readonly value: boolean
  readonly baseline: boolean
  readonly overridden: boolean
}

/** Server-resolved flags at page load, before any in-session override. */
function readBaseline(): Record<string, boolean> {
  if (typeof window === 'undefined') return {}
  const injected = (window as unknown as { INITIAL_STATE?: InjectedState })
    .INITIAL_STATE
  return { ...injected?.featureFlags }
}

export interface UseFlagOverrides {
  readonly rows: ReadonlyArray<FlagRow>
  readonly overriddenCount: number
  readonly setFlag: (key: string, value: boolean) => void
  readonly toggleFlag: (key: string) => void
  readonly resetFlag: (key: string) => void
  readonly resetAll: () => void
}

export function useFlagOverrides(): UseFlagOverrides {
  const dispatch = useDispatch()
  const flags = useSelector((s: StoreShape) => s.featureFlags) ?? EMPTY_FLAGS

  // Capture the server baseline exactly once — the first render owns truth.
  const baselineRef = useRef<Record<string, boolean> | null>(null)
  if (baselineRef.current === null) baselineRef.current = readBaseline()
  const baseline = baselineRef.current

  const setFlag = useCallback(
    (key: string, value: boolean) => {
      dispatch(storeFlags({ [key]: value }))
    },
    [dispatch]
  )

  const rows = useMemo<ReadonlyArray<FlagRow>>(() => {
    // Union of baseline + live keys so a flag added at runtime still shows.
    const keys = Array.from(
      new Set([...Object.keys(baseline), ...Object.keys(flags)])
    ).sort()
    return keys.map(key => {
      const value = Boolean(flags[key])
      const base = Boolean(baseline[key])
      return { key, value, baseline: base, overridden: value !== base }
    })
  }, [flags, baseline])

  const resetFlag = useCallback(
    (key: string) => setFlag(key, Boolean(baseline[key])),
    [setFlag, baseline]
  )

  const resetAll = useCallback(() => {
    const restore: Record<string, boolean> = {}
    rows.forEach(r => {
      if (r.overridden) restore[r.key] = r.baseline
    })
    if (Object.keys(restore).length > 0) {
      dispatch(storeFlags(restore))
    }
  }, [dispatch, rows])

  const toggleFlag = useCallback(
    (key: string) => {
      const current = Boolean(flags[key])
      setFlag(key, !current)
    },
    [flags, setFlag]
  )

  const overriddenCount = useMemo(
    () => rows.reduce((n, r) => n + (r.overridden ? 1 : 0), 0),
    [rows]
  )

  return { rows, overriddenCount, setFlag, toggleFlag, resetFlag, resetAll }
}
